import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { isNotNull } from '@/utils/helpers';
import { RootState } from '../../store';
import {
    deleteAdminQuizV2,
    postMyQuizzesV2Search,
    CurrentUserQuizzesV2SearchRequestBody,
} from '@/features/Quiz/api/getQuizzes';
import { MyQuizV2ListItem } from '@/features/Quiz/types';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';

const PAGE_SIZE = 20;

export const getAllMyQuizzesV2 = createAsyncThunk<
    { items: MyQuizV2ListItem[]; page: number; total: number; totalPages: number },
    {
        activePage: number;
        activeSearchKeyword?: string;
    },
    { rejectValue: string }
>('quizzes/getAllMyQuizzesV2', async ({ activePage, activeSearchKeyword }) => {
    const body: CurrentUserQuizzesV2SearchRequestBody = {
        page: activePage - 1,
        size: PAGE_SIZE,
        sort: [['id', 'desc']],
        ...(activeSearchKeyword?.trim() ? { title: activeSearchKeyword.trim() } : {}),
    };
    const res = await postMyQuizzesV2Search(body);
    const totalPages = res.size > 0 ? Math.max(1, Math.ceil(res.total / res.size)) : 1;

    return {
        items: res.data ?? [],
        page: activePage,
        total: res.total,
        totalPages,
    };
});

export const removeMyQuizV2 = createAsyncThunk(
    'quizzes/removeMyQuizV2',
    async ({ quizUuid, id }: { quizUuid: string; id: number }) => {
        await deleteAdminQuizV2(quizUuid);
        return id;
    }
);

const quizMyV2Adapter = createEntityAdapter<MyQuizV2ListItem>();

type State = {
    pages: Record<number, MyQuizV2ListItem['id'][]>;
    activePage: number;
    total: number;
    totalPages: number;
    isLoading: boolean;
};

const quizMyV2Slice = createSlice({
    name: 'quizMyV2',
    initialState: quizMyV2Adapter.getInitialState<State>({
        pages: {},
        activePage: 1,
        totalPages: 1,
        total: 0,
        isLoading: false,
    }),
    reducers: {
        setQuizMyV2ActivePage: (state, action: PayloadAction<number>) => {
            state.activePage = action.payload;
        },
        resetQuizMyV2Pages: (state) => {
            state.pages = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAllMyQuizzesV2.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(getAllMyQuizzesV2.fulfilled, (state, action) => {
            quizMyV2Adapter.upsertMany(state, action.payload.items);
            state.pages[action.payload.page] = action.payload.items.map((item) => item.id);
            state.total = action.payload.total;
            state.activePage = action.payload.page;
            state.totalPages = action.payload.totalPages;
            state.isLoading = false;
        });
        builder.addCase(getAllMyQuizzesV2.rejected, (state) => {
            state.isLoading = false;
        });
        builder.addCase(removeMyQuizV2.fulfilled, (state, action) => {
            quizMyV2Adapter.removeOne(state, action.payload);
        });
    },
});

export const quizMyV2 = quizMyV2Slice.reducer;
export const { resetQuizMyV2Pages, setQuizMyV2ActivePage } = quizMyV2Slice.actions;

const selectState = (state: RootState) => state.quizzes.quizMyV2;

export const { selectById: selectQuizMyV2ById } = quizMyV2Adapter.getSelectors(selectState);

const selectRootState = (state: RootState) => state;

const selectIdsByPage = createSelector(
    [selectState, (_: RootState, page: number) => page],
    (state, page) => state.pages[page] ?? []
);

export const selectQuizMyV2Page = createSelector(
    [selectRootState, (state: RootState, page: number) => selectIdsByPage(state, page)],
    (state, ids) => ids.map((id) => selectQuizMyV2ById(state, id)).filter(isNotNull)
);

export const selectQuizMyV2ActivePage = createSelector([selectState], (state) => state.activePage);
export const selectQuizMyV2TotalPages = createSelector([selectState], (state) => state.totalPages);
export const selectQuizMyV2Loading = createSelector([selectState], (state) => state.isLoading);
